import type { VenueOperationVerification } from './managerVenueTypes'

/**
 * `GET/PUT /venue/v1/manager/venues/{venueId}/profile` 응답의 소비 타입 — **#594 §1·§2·§4·§5 계약의
 * 사본**이다. `managerVenueTypes.ts`·`dashboardTypes.ts` 와 같은 규칙 — 서버가 계약을 바꾸지 않는 한
 * 여기서 필드를 파생하거나 이름을 바꾸지 않는다.
 */

export interface VenueProfileBasic {
  name: string
  introduction: string | null
  /** 🚨 `#747` 로 nullable 이 됐다(무료 등록 건은 미입력 가능) — 저장(`PUT`)에서는 여전히 필수다. */
  contact: string | null
  websiteUrl: string | null
}

export interface VenueProfileLocation {
  /**
   * 🔒 이 화면은 저장 시 보내지 않는다(`#875` 자율 결정) — `#866` 로 서버는 `PUT .../profile` 에서
   * `stateId`/`cityId` 를 이미 받지만(구 주석의 "읽기 전용" 은 더 이상 사실이 아니다), 다음 우편번호가
   * 주는 한글 지역명을 서버 코드 체계(`CN`/`CN_CHEONAN` 류, 이 저장소의 `regionCodes.ts` 법정동코드와
   * 다른 카탈로그)로 옮길 매핑표가 없고 서버도 카탈로그 실재 검증을 하지 않는다 — 잘못 매핑하면 조용히
   * 틀린 값이 저장된다. 미전송 시 서버가 기존 값을 보존하므로(마이그레이션 백필값 유지) 안 보내는
   * 쪽을 택했다. 매핑표가 게시되면 별도 태스크로 전송을 추가한다.
   */
  stateId: string
  /** 🔒 위 `stateId` 와 같은 이유로 저장 시 보내지 않는다. */
  cityId: string
  address: string
  detailAddress: string | null
  parkingGuide: string | null
  /** 🚨 `#865` 로 3상태(`null`=미입력)가 폐기되고 non-null 로 확정됐다 — 저장은 항상 이 값을 채워 보낸다. */
  parkingAvailable: boolean
  /** 🚨 신규(`#866`) — 쌍으로만 유효하다. 한쪽만 채워진 상태는 만들지 않는다(주소 검색으로만 함께 채운다). */
  latitude: number | null
  longitude: number | null
}

/** 서버가 준 라벨 그대로 쓴다(C4) — 화면이 코드로 라벨을 다시 조립하지 않는다. */
export interface VenueAmenityItem {
  code: string
  label: string
}

export interface VenueProfileFacility {
  /** 🚨 코트별 구조화 `floor_type` 과 다른 축이다 — 구장 대표 안내 문구일 뿐 예약 로직에 쓰지 않는다. */
  floorMaterialNotice: string | null
  /** 🚨 `#865` 로 3상태(`null`=미입력)가 폐기되고 non-null 로 확정됐다. */
  showerAvailable: boolean
  /** 🚨 `#865` 로 3상태(`null`=미입력)가 폐기되고 non-null 로 확정됐다. */
  lockerRoomAvailable: boolean
  /**
   * 🚨 신규(`#865`) — 스트링 작업 가능 여부. 편의시설 칩(`amenities`)이 아니라 주차·샤워실·탈의실과
   * 같은 축의 전용 필드다(칩은 "없음"을 표현하지 못해 채택되지 않았다 — 백엔드 D-A1).
   */
  stringServiceAvailable: boolean
  /** 미입력은 빈 배열(`null` 아님). */
  amenities: VenueAmenityItem[]
  /** 미입력은 빈 배열. */
  rentalEquipments: string[]
}

/** 🔒 읽기 전용 — 취소·환불 규정 변경은 이 화면 소관이 아니다(#594 D1). */
export interface VenueCancellationPolicySummary {
  /** 🚨 `false` 면 아래 3개가 전부 `null` — `summaryText == null` 로 미설정을 판단하지 않는다. */
  configured: boolean
  summaryText: string | null
  cancellableUntilLabel: string | null
  feeLabel: string | null
}

export interface VenueProfileOperation {
  usageGuide: string | null
  additionalNotice: string | null
  cancellationPolicySummary: VenueCancellationPolicySummary
}

/** 캐러셀 원소(#618 §2). */
export interface VenueImageItem {
  imageUrl: string
  thumbnailUrl: string | null
  displayOrder: number
}

/**
 * `GET` 응답의 사진 블록. `#865` 로 "대표 = 배열 0번째" 불변식이 폐기되고 **대표/시설 두 축이
 * 독립**됐다 — `imageUrl`/`thumbnailUrl`(대표, 0또는1장)과 `facilityImages`(시설, 최대 5장)를
 * 각각 저장·조회한다. 🚨 서버는 하위호환을 위해 레거시 `images`(둘을 합성한 목록)도 계속 내려주지만,
 * 이 화면은 신규 축만 소비한다(레거시 필드는 타입에 선언하지 않는다 — 자율 결정 10).
 */
export interface VenueProfileImage {
  imageUrl: string | null
  thumbnailUrl: string | null
  /** 미입력은 빈 배열(`null` 아님). 최대 5장. */
  facilityImages: VenueImageItem[]
}

export interface VenueProfileSummary {
  /** `venue_court` 실집계(삭제 제외) — 등록 신청서 스냅샷이 아니다. */
  courtCount: number
  /** 기본정보 마지막 수정일(없으면 등록일). */
  lastModifiedAt: string
  /** `null` = 한 번도 저장한 적 없음. */
  profileUpdatedAt: string | null
  reviewStatus: 'PENDING' | 'APPROVED' | 'REJECTED'
  /** 🔒 읽기 전용 — 노출 중단은 운영자의 민원·제재 수단이다(#594 D2). */
  exposureStatus: 'EXPOSED' | 'HIDDEN'
  userVisible: boolean
  operationVerification: VenueOperationVerification
  reservationEnabled: boolean
}

export interface VenueProfileView {
  /** 이 응답 모든 상태 판정의 기준시각. */
  baseTime: string
  venueId: string
  basic: VenueProfileBasic
  location: VenueProfileLocation
  facility: VenueProfileFacility
  operation: VenueProfileOperation
  image: VenueProfileImage
  summary: VenueProfileSummary
  /** 저장을 제한하는 사유가 있을 때만 문자열, 아니면 `null`. 문구는 서버가 만든다(C4). */
  saveNotice: string | null
}

/** `images[]` 원소 저장 바디(#618 §1-1). `thumbnailUrl` 은 선택 — 없으면 키 자체를 생략해도 된다. */
export interface VenueImageSaveItem {
  imageUrl: string
  thumbnailUrl?: string | null
}

/**
 * `PUT` 요청 바디 — **전량 교체**(#594 §2 D5, PATCH 아님). 생략한 필드는 미입력(`null`/`false`)으로
 * 저장되므로 화면은 항상 폼 전체를 보낸다.
 *
 * 🚨 `facilityImages` 만 유일한 예외다(#865 §6-B, 레거시 `images` 예외를 이름만 승계) — 키를 아예
 * 빼면 "기존 시설 사진 보존", `[]` 는 "전부 삭제", 배열을 채우면 "그 배열로 통째 교체"다.
 * `imageUrl`/`thumbnailUrl`(대표 1장)·`latitude`/`longitude`는 이 예외 대상이 아니라 다른 필드처럼
 * 매번 채워 보낸다.
 */
export interface VenueProfileSaveInput {
  name: string
  introduction: string | null
  contact: string
  websiteUrl: string | null
  address: string
  detailAddress: string | null
  parkingGuide: string | null
  /** 🚨 미전송 시 서버가 `false` 로 저장한다(non-null 확정, #865) — 화면은 항상 채워 보낸다. */
  parkingAvailable: boolean
  /** 🚨 쌍으로만 유효. 주소를 검색으로 채운 뒤에만 값이 있고, 수동 편집 시 함께 `null` 로 지운다. */
  latitude: number | null
  longitude: number | null
  floorMaterialNotice: string | null
  showerAvailable: boolean
  lockerRoomAvailable: boolean
  stringServiceAvailable: boolean
  /** `VenueAmenity.code[]`. */
  amenities: string[]
  rentalEquipments: string[]
  usageGuide: string | null
  additionalNotice: string | null
  imageUrl: string | null
  thumbnailUrl: string | null
  facilityImages?: VenueImageSaveItem[]
}

/**
 * `#594` §4 편의시설 카탈로그 — forward-only(값 삭제·개명 금지).
 *
 * 🚨 `GET` 응답의 `facility.amenities[]` 는 "이 구장이 **선택한** 것"만 준다. "선택 가능한 전체
 * 목록"은 응답에 없으므로 이 상수를 선택 UI 를 그리는 값 목록으로 쓴다(`passTypes.ts` 가 상태
 * 열거값을 계약 문서에서 그대로 옮긴 것과 같은 이유).
 *
 * 🚫 주차장·샤워실·탈의실·대여 장비는 **의도적으로 여기 없다** — 각각 전용 필드
 * (`parkingAvailable`·`showerAvailable`·`lockerRoomAvailable`·`rentalEquipments`)가 정본이다.
 * 같은 사실을 두 곳에 저장하면 값이 갈리는 사고가 난다(#594 §4).
 */
export const VENUE_AMENITY_CATALOG: VenueAmenityItem[] = [
  { code: 'SHUTTLECOCK_SALE', label: '셔틀콕 판매' },
  { code: 'VENDING_MACHINE', label: '음료 자판기' },
  { code: 'AIR_CONDITIONING', label: '냉난방' },
  { code: 'WIFI', label: '무선 인터넷' },
  { code: 'LOUNGE', label: '휴게 공간' },
  { code: 'ELEVATOR', label: '엘리베이터' },
  { code: 'RESTROOM', label: '화장실' },
  { code: 'WATER_PURIFIER', label: '정수기' },
]

/** `#594` §5 — forward-only. 모르는 코드는 화면이 재해석하지 않고 `message` 를 그대로 노출한다. */
export type VenueProfileFieldErrorCode =
  | 'REQUIRED'
  | 'TOO_LONG'
  | 'INVALID_FORMAT'
  | 'TOO_MANY_ITEMS'
  | 'BLANK_ITEM'
  | 'UNKNOWN_VALUE'
